import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Mail, Plus, RefreshCw, Trash2, Paperclip, Loader2, Inbox, Send, FileText, Archive, AlertOctagon, Folder, ChevronLeft, ChevronRight, Download, Star } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const ROLE_ICON = { inbox: Inbox, sent: Send, drafts: FileText, archive: Archive, spam: AlertOctagon, trash: Trash2 };

function AddAccountForm({ onDone, onCancel }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ email: "", password: "", display_name: "", imap_host: "", imap_port: 993, smtp_host: "", smtp_port: 587 });
  const [hint, setHint] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const detect = async () => {
    if (!f.email.includes("@")) return;
    try {
      const { data } = await api.get("/mail/providers/detect", { params: { email_addr: f.email } });
      setF((p) => ({ ...p, imap_host: data.imap_host, imap_port: data.imap_port, smtp_host: data.smtp_host, smtp_port: data.smtp_port }));
      setHint(data.hint || "");
    } catch { /* manual */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/mail/accounts", { ...f, imap_port: Number(f.imap_port), smtp_port: Number(f.smtp_port) });
      toast.success(t("mail.connected", "Cont conectat"));
      onDone(data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="gold-card space-y-3" style={{ padding: "1.2rem" }} data-testid="mail-add-form">
      <h2 style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a" }}>{t("mail.addAccount", "Conectează un e-mail")}</h2>
      <p className="kv-label" style={{ fontSize: "0.7rem" }}>{t("mail.addHelp", "Folosește o parolă de aplicație (Gmail, iCloud, Yahoo) sau parola IMAP a domeniului tău. Datele sunt criptate și vizibile doar ție.")}</p>
      <div className="grid gap-3 md:grid-cols-2">
        <input className="input-lodge" placeholder="email@exemplu.ro" value={f.email} onChange={set("email")} onBlur={detect} required data-testid="mail-email-input" />
        <input className="input-lodge" type="password" placeholder={t("mail.appPassword", "Parolă de aplicație")} value={f.password} onChange={set("password")} required data-testid="mail-password-input" />
        <input className="input-lodge" placeholder={t("mail.displayName", "Nume afișat (opțional)")} value={f.display_name} onChange={set("display_name")} data-testid="mail-display-name-input" />
        <div />
        <input className="input-lodge" placeholder="imap.server.ro" value={f.imap_host} onChange={set("imap_host")} required data-testid="mail-imap-host-input" />
        <input className="input-lodge" placeholder="993" value={f.imap_port} onChange={set("imap_port")} data-testid="mail-imap-port-input" />
        <input className="input-lodge" placeholder="smtp.server.ro" value={f.smtp_host} onChange={set("smtp_host")} required data-testid="mail-smtp-host-input" />
        <input className="input-lodge" placeholder="587" value={f.smtp_port} onChange={set("smtp_port")} data-testid="mail-smtp-port-input" />
      </div>
      {hint ? <p style={{ fontSize: "0.8rem", color: "#7a6428" }} data-testid="mail-provider-hint">{hint}</p> : null}
      <div className="flex gap-2 justify-end">
        <button type="button" className="btn-outline-gold" onClick={onCancel} data-testid="mail-add-cancel">{t("common.cancel", "Anulează")}</button>
        <button type="submit" className="btn-primary-gold flex items-center gap-2" disabled={busy} data-testid="mail-add-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {t("mail.connect", "Conectează și testează")}
        </button>
      </div>
    </form>
  );
}

export default function MailPage() {
  const { t, i18n } = useTranslation();
  const [accounts, setAccounts] = useState([]);
  const [accId, setAccId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("INBOX");
  const [list, setList] = useState({ items: [], total: 0, page: 1, limit: 30 });
  const [loadingList, setLoadingList] = useState(false);
  const [msg, setMsg] = useState(null);
  const [loadingMsg, setLoadingMsg] = useState(false);

  const loadAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/mail/accounts");
      setAccounts(data);
      if (data.length && !data.some((a) => a.id === accId)) setAccId(data[0].id);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [accId]);
  useEffect(() => { loadAccounts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFolders = useCallback(async () => {
    if (!accId) return;
    try { const { data } = await api.get(`/mail/accounts/${accId}/folders`); setFolders(data); } catch (err) { toast.error(formatApiError(err)); }
  }, [accId]);

  const loadList = useCallback(async (page = 1) => {
    if (!accId) return;
    setLoadingList(true);
    try { const { data } = await api.get(`/mail/accounts/${accId}/messages`, { params: { folder, page, limit: 30 } }); setList(data); }
    catch (err) { toast.error(formatApiError(err)); } finally { setLoadingList(false); }
  }, [accId, folder]);

  useEffect(() => { setFolder("INBOX"); setMsg(null); loadFolders(); }, [accId, loadFolders]);
  useEffect(() => { setMsg(null); loadList(1); }, [folder, loadList]);

  const openMsg = async (m) => {
    setLoadingMsg(true);
    try {
      const { data } = await api.get(`/mail/accounts/${accId}/messages/${m.uid}`, { params: { folder } });
      setMsg(data);
      setList((p) => ({ ...p, items: p.items.map((x) => (x.uid === m.uid ? { ...x, seen: true } : x)) }));
      if (!m.seen) setFolders((p) => p.map((f) => (f.name === folder ? { ...f, unread: Math.max(0, f.unread - 1) } : f)));
    } catch (err) { toast.error(formatApiError(err)); } finally { setLoadingMsg(false); }
  };

  const download = async (a) => {
    try {
      const res = await api.get(`/mail/accounts/${accId}/messages/${msg.uid}/attachments/${a.index}`, { params: { folder }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const el = document.createElement("a"); el.href = url; el.download = a.filename; document.body.appendChild(el); el.click(); el.remove(); URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeAccount = async (a) => {
    if (!window.confirm(t("mail.removeConfirm", "Deconectezi contul {{email}}? Mesajele rămân pe server.", { email: a.email }))) return;
    try { await api.delete(`/mail/accounts/${a.id}`); setAccId(null); await loadAccounts(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(i18n.language, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
  const pages = Math.max(1, Math.ceil(list.total / list.limit));

  return (
    <div className="page-pad" data-testid="mail-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="page-title flex items-center gap-2"><Mail size={22} className="text-gold-500" /> {t("mail.title", "E-mail")}</h1>
          <p className="page-subtitle">{t("mail.subtitle", "Căsuța ta poștală, conectată în siguranță la contul tău de lojă.")}</p>
        </div>
        {!adding && (
          <button type="button" className="btn-primary-gold flex items-center gap-2" onClick={() => setAdding(true)} data-testid="mail-add-btn">
            <Plus size={16} /> {t("mail.addAccount", "Conectează un e-mail")}
          </button>
        )}
      </div>
      {adding && <div className="mb-4"><AddAccountForm onDone={async (a) => { setAdding(false); await loadAccounts(); setAccId(a.id); }} onCancel={() => setAdding(false)} /></div>}
      {!accounts.length && !adding ? (
        <div className="gold-card" style={{ padding: "2rem", textAlign: "center" }} data-testid="mail-empty">
          <Mail size={48} color="#c9a84c" style={{ opacity: 0.5, display: "block", margin: "0 auto 0.8rem" }} />
          <p style={{ color: "#34322d" }}>{t("mail.noAccounts", "Niciun e-mail conectat încă.")}</p>
        </div>
      ) : accounts.length ? (
        <div className="grid gap-3" style={{ gridTemplateColumns: "230px 360px 1fr", minHeight: "70vh" }} data-testid="mail-layout">
          <div className="gold-card" style={{ padding: "0.8rem" }}>
            <select className="input-lodge w-full mb-2" value={accId || ""} onChange={(e) => setAccId(e.target.value)} data-testid="mail-account-select">
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.email}</option>)}
            </select>
            <div className="flex gap-2 mb-3">
              <button type="button" className="btn-outline-gold flex-1 flex items-center justify-center gap-1" onClick={() => { loadFolders(); loadList(1); }} data-testid="mail-refresh-btn"><RefreshCw size={13} /></button>
              <button type="button" className="btn-outline-gold flex items-center justify-center" style={{ color: "#7c2a2a", borderColor: "#7c2a2a" }} onClick={() => removeAccount(accounts.find((a) => a.id === accId))} data-testid="mail-remove-account-btn"><Trash2 size={13} /></button>
            </div>
            {folders.map((f) => {
              const Icon = ROLE_ICON[f.role] || Folder;
              const active = f.name === folder;
              return (
                <button key={f.name} type="button" onClick={() => setFolder(f.name)} data-testid={`mail-folder-${f.role || "custom"}`}
                  className="w-full flex items-center justify-between gap-2 text-left"
                  style={{ padding: "0.45rem 0.6rem", borderRadius: 6, background: active ? "rgba(201,168,76,0.18)" : "transparent", color: "#0d1b2a", fontWeight: f.unread ? 700 : 500, fontSize: "0.88rem" }}>
                  <span className="flex items-center gap-2 truncate"><Icon size={14} color="#9a7530" /> {f.role ? t(`mail.role_${f.role}`, f.name) : f.name}</span>
                  {f.unread ? <span style={{ fontSize: "0.7rem", background: "#c9a84c", color: "#0d1b2a", borderRadius: 999, padding: "0 6px" }}>{f.unread}</span> : null}
                </button>
              );
            })}
          </div>
          <div className="gold-card" style={{ padding: 0, overflow: "hidden" }} data-testid="mail-list">
            <div className="flex items-center justify-between" style={{ padding: "0.5rem 0.8rem", borderBottom: "1px solid rgba(201,168,76,0.3)" }}>
              <span className="kv-label" style={{ fontSize: "0.7rem" }}>{list.total} {t("mail.messages", "mesaje")}</span>
              <span className="flex items-center gap-1">
                <button type="button" disabled={list.page <= 1} onClick={() => loadList(list.page - 1)} style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="mail-prev-page"><ChevronLeft size={16} /></button>
                <span className="kv-label" style={{ fontSize: "0.7rem" }}>{list.page}/{pages}</span>
                <button type="button" disabled={list.page >= pages} onClick={() => loadList(list.page + 1)} style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="mail-next-page"><ChevronRight size={16} /></button>
              </span>
            </div>
            {loadingList ? <div style={{ padding: "2rem", textAlign: "center", color: "#9a7530" }}><Loader2 className="animate-spin inline" size={20} /></div> : list.items.map((m) => (
              <button key={m.uid} type="button" onClick={() => openMsg(m)} className="w-full text-left" data-testid={`mail-item-${m.uid}`}
                style={{ padding: "0.6rem 0.8rem", borderBottom: "1px solid rgba(201,168,76,0.15)", background: msg?.uid === m.uid ? "rgba(201,168,76,0.14)" : "transparent" }}>
                <div className="flex justify-between gap-2" style={{ fontSize: "0.82rem", fontWeight: m.seen ? 500 : 800, color: "#0d1b2a" }}>
                  <span className="truncate">{m.from}</span><span style={{ whiteSpace: "nowrap", fontSize: "0.7rem", color: "#7a6428" }}>{fmtDate(m.date)}</span>
                </div>
                <div className="flex items-center gap-1 truncate" style={{ fontSize: "0.85rem", color: "#34322d", fontWeight: m.seen ? 400 : 700 }}>
                  {m.flagged ? <Star size={12} color="#c9a84c" /> : null}{m.has_attachments ? <Paperclip size={12} /> : null}<span className="truncate">{m.subject}</span>
                </div>
              </button>
            ))}
          </div>
          <div className="gold-card" style={{ padding: "1rem", overflow: "auto" }} data-testid="mail-reader">
            {loadingMsg ? <div style={{ textAlign: "center", color: "#9a7530", padding: "2rem" }}><Loader2 className="animate-spin inline" size={20} /></div>
              : !msg ? <p className="kv-label" style={{ textAlign: "center", marginTop: "3rem" }}>{t("mail.selectMessage", "Alege un mesaj din listă.")}</p> : (
                <>
                  <h2 style={{ fontFamily: "Cinzel, serif", fontSize: "1.1rem", color: "#0d1b2a" }} data-testid="mail-reader-subject">{msg.subject}</h2>
                  <div style={{ fontSize: "0.8rem", color: "#34322d", margin: "0.4rem 0 0.8rem" }}>
                    <div><b>{t("mail.from", "De la")}:</b> {msg.from}</div>
                    <div><b>{t("mail.to", "Către")}:</b> {msg.to}{msg.cc ? ` · Cc: ${msg.cc}` : ""}</div>
                    <div className="kv-label" style={{ fontSize: "0.68rem" }}>{fmtDate(msg.date)}</div>
                  </div>
                  {msg.attachments?.length ? (
                    <div className="flex flex-wrap gap-2 mb-3" data-testid="mail-attachments">
                      {msg.attachments.map((a) => (
                        <button key={a.index} type="button" className="btn-outline-gold flex items-center gap-1" style={{ fontSize: "0.75rem" }} onClick={() => download(a)} data-testid={`mail-attachment-${a.index}`}>
                          <Download size={12} /> {a.filename} <span className="kv-label" style={{ fontSize: "0.62rem" }}>({Math.round(a.size / 1024)} KB)</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {msg.html ? (
                    <iframe title="mail-body" sandbox="" srcDoc={msg.html} style={{ width: "100%", minHeight: "55vh", border: "1px solid rgba(201,168,76,0.3)", background: "#fff", borderRadius: 6 }} data-testid="mail-body-html" />
                  ) : (
                    <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: "0.9rem", color: "#34322d" }} data-testid="mail-body-text">{msg.text || ""}</pre>
                  )}
                </>
              )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
